/**
 * Ordering over WhatsApp.
 *
 * The order is saved on our side first and then handed to the shopper as a
 * pre-filled WhatsApp message to the merchant, who confirms it and arranges
 * payment in the chat. Nothing is charged here, and nothing that comes back
 * from WhatsApp is treated as proof of anything - the order exists whether or
 * not the message is ever sent.
 *
 * The live server writes the message itself and hands the URL back with the
 * order; the builder below is for a shop with no server behind it, which is
 * what a theme preview is.
 */

import { linkPreviewUrl } from '@/lib/imageRenditions';
import { formatMoney } from '@/lib/money';
import { internationalPhone } from '@/lib/phone';

/** The payload `POST /storefront/checkout` returns for a WhatsApp order. */
export interface WhatsAppPaymentPayload {
  kind: 'whatsapp';
  whatsapp_url: string;
  whatsapp_phone?: string;
  message?: string;
}

export function isWhatsAppPayload(value: unknown): value is WhatsAppPaymentPayload {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return candidate.kind === 'whatsapp' && typeof candidate.whatsapp_url === 'string';
}

/**
 * A `wa.me` link that opens a chat with `phone` and `message` already typed.
 *
 * `wa.me` wants the number as bare digits with the country code and nothing
 * else - no plus, no spaces, no dashes - so whatever a merchant typed into
 * their settings is reduced to that here.
 */
export function whatsAppUrl(phone: string, message: string): string {
  const digits = phone.replace(/\D/g, '');
  return `https://wa.me/${digits}?text=${encodeURIComponent(message)}`;
}

/**
 * A chat link for a number a merchant is looking at, or null if there is none.
 *
 * `wa.me` will not infer a country: a ten-digit number handed over as-is opens
 * a chat with whoever owns those digits in the viewer's own country, or with
 * nobody. So the number goes through `internationalPhone` first, and one that
 * cannot be made international gets no link at all.
 *
 * No message is attached - the merchant is starting the conversation and will
 * write their own, unlike a shopper's order handoff, which is `whatsAppUrl`.
 */
export function whatsAppChatUrl(phone: string | null | undefined): string | null {
  const digits = internationalPhone(phone);
  return digits === null ? null : `https://wa.me/${digits}`;
}

/**
 * A `wa.me` link that opens WhatsApp with `text` typed and no recipient chosen.
 *
 * The shopper picks who to send it to: this is a product page being passed on
 * to a friend, not a message to the merchant, so no number goes in the link.
 */
export function whatsAppShareUrl(text: string): string {
  return `https://wa.me/?text=${encodeURIComponent(text)}`;
}

/**
 * The longest `wa.me` link we will hand a browser, in characters.
 *
 * Mirrors `WHATSAPP_URL_BUDGET` on the server, and is set by the same thing:
 * not the browser, which is generous, but `wa.me` itself - a real address whose
 * servers parse the request line when the desktop fallback is fetched, and
 * request lines are conventionally capped at 8 KB. Percent-encoding is what
 * makes this tight: a 132-character CDN address costs 155 in the query string.
 */
export const WHATSAPP_URL_BUDGET = 4000;

export interface WhatsAppOrderLine {
  product_name: string;
  options: Record<string, unknown>;
  quantity: number;
  unit_price: string;
  line_subtotal: string;
  /** The product's photo, absolute. Sent to the merchant as a tappable link. */
  image_url?: string | null;
}

export interface WhatsAppOrderMessageInput {
  storeName: string;
  /** The shop's own mark, absolute. Sits under its name like a letterhead. */
  storeLogoUrl?: string | null;
  orderNumber: string;
  customer: { name: string; email: string; phone?: string };
  address: { line1: string; line2?: string; city: string; state: string; postal_code: string };
  items: WhatsAppOrderLine[];
  subtotal: string;
  discount_amount: string;
  discount_code?: string | null;
  shipping_amount: string;
  tax_amount: string;
  payment_fee_amount?: string | null;
  gift_card_amount?: string | null;
  total: string;
  currency?: string;
}

/** `Indigo Kurta (Size M, Colour Indigo) × 2 — ₹1,000.00` */
function describeLine(item: WhatsAppOrderLine): string {
  const chosen = Object.values(item.options).map(String).filter(Boolean);
  const variant = chosen.length > 0 ? ` (${chosen.join(', ')})` : '';
  return `• ${item.product_name}${variant} × ${item.quantity} — ${formatMoney(item.line_subtotal)}`;
}

/**
 * The address to put in the chat for `url`, or null if there is none.
 *
 * Two things happen here. Only absolute `http(s)` addresses survive - a
 * relative path pasted into a chat is a dead string, not a picture. And a
 * laddered image is swapped for its JPEG copy, because the ladder is WebP and
 * WhatsApp will not draw a thumbnail from WebP: it fetches the address, fails
 * to decode it, and shows the card with the domain in it and no picture.
 */
function webUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  if (!url.startsWith('http://') && !url.startsWith('https://')) return null;
  return linkPreviewUrl(url) ?? url;
}

/** The line's photo, if there is one to send. */
function photoLink(item: WhatsAppOrderLine): string | null {
  return webUrl(item.image_url);
}

/**
 * The message a shopper sends the merchant, as plain text with line breaks.
 *
 * Readable on a phone rather than machine-parseable: this is what the
 * merchant reads in their chat, and what the shopper sees before they press
 * send. The totals mirror the checkout summary line for line, so the number
 * they agreed to is the number they send.
 *
 * `maxPhotos` caps how many image addresses are written out, and the message
 * says how many it held back. Callers do not pick it: `whatsAppOrderLink` works
 * it out from `WHATSAPP_URL_BUDGET`, because whether a photo fits is a fact
 * about the finished URL. Undefined means every photo.
 */
export function buildWhatsAppOrderMessage(
  input: WhatsAppOrderMessageInput,
  maxPhotos?: number,
): string {
  const lines: string[] = [];

  lines.push(`Hello ${input.storeName}!`);
  lines.push(`New order ${input.orderNumber} from ${input.storeName}`);
  // The shop's own mark, the way a letterhead sits at the top of an invoice.
  // Being the first address in the message it is also the one WhatsApp draws a
  // preview card for.
  const logo = webUrl(input.storeLogoUrl);
  if (logo) lines.push(logo);
  lines.push('');

  lines.push('Customer');
  lines.push(input.customer.name);
  lines.push(input.customer.email);
  if (input.customer.phone) lines.push(input.customer.phone);
  lines.push('');

  lines.push('Deliver to');
  lines.push(input.address.line1);
  if (input.address.line2) lines.push(input.address.line2);
  lines.push(`${input.address.city}, ${input.address.state} ${input.address.postal_code}`);
  lines.push('');

  lines.push('Items');
  let photosShown = 0;
  let photosWithheld = 0;
  for (const item of input.items) {
    lines.push(describeLine(item));
    const photo = photoLink(item);
    if (photo === null) continue;
    if (maxPhotos !== undefined && photosShown >= maxPhotos) {
      photosWithheld += 1;
      continue;
    }
    lines.push(`  ${photo}`);
    photosShown += 1;
  }
  if (photosWithheld > 0) {
    lines.push(`(${photosWithheld} more item photos in the order - ask and I'll send them.)`);
  }
  lines.push('');

  lines.push(`Subtotal: ${formatMoney(input.subtotal)}`);
  if (Number(input.discount_amount) > 0) {
    const code = input.discount_code ? ` (${input.discount_code})` : '';
    lines.push(`Discount${code}: -${formatMoney(input.discount_amount)}`);
  }
  lines.push(
    `Shipping: ${Number(input.shipping_amount) > 0 ? formatMoney(input.shipping_amount) : 'Free'}`,
  );
  lines.push(`Tax/GST: ${formatMoney(input.tax_amount)}`);
  if (input.payment_fee_amount && Number(input.payment_fee_amount) > 0) {
    lines.push(`COD fee: ${formatMoney(input.payment_fee_amount)}`);
  }
  if (input.gift_card_amount && Number(input.gift_card_amount) > 0) {
    lines.push(`Gift card: -${formatMoney(input.gift_card_amount)}`);
  }
  lines.push(`Grand total: ${formatMoney(input.total)}`);

  return lines.join('\n');
}

/**
 * The chat link for an order, with as many item photos as the URL can hold.
 *
 * Built and measured rather than counted: percent-encoding decides the cost and
 * it depends on the addresses themselves, so the only honest test of whether a
 * photo fits is the length of the finished link. Photos are the elastic part
 * and the only part that gives - a big order loses pictures before it loses
 * lines, because a merchant can ask for a photo and cannot guess at an item
 * that was never sent. An order long enough to burst the budget on its item
 * lines alone goes out whole and over-length; WhatsApp itself will still carry
 * it, its own ceiling being 65,536 characters.
 */
export function whatsAppOrderLink(phone: string, input: WhatsAppOrderMessageInput): string {
  let photos = input.items.filter((item) => photoLink(item) !== null).length;
  let url = whatsAppUrl(phone, buildWhatsAppOrderMessage(input));
  while (photos > 0 && url.length > WHATSAPP_URL_BUDGET) {
    photos -= 1;
    url = whatsAppUrl(phone, buildWhatsAppOrderMessage(input, photos));
  }
  return url;
}
